<script setup lang="ts">
import { TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {
  SCOPE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  cancelStocktake,
  confirmStocktake,
  countStocktake,
  diffOf,
  getStocktake,
  putStocktakeItem,
  startStocktake,
  type Detail,
  type ItemDetail,
  type StocktakeStatus,
} from '../api/stocktakes'

const route = useRoute()
const router = useRouter()

const detail = ref<Detail | null>(null)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')
const busy = ref(false)

const takeId = computed(() => Number(route.params.id))
const take = computed(() => detail.value?.take ?? null)
const items = computed(() => detail.value?.items ?? [])

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    detail.value = await getStocktake(takeId.value)
    notice.value = noticeText
  } catch (e) {
    error.value = e
    detail.value = null
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})

function back(): void {
  void router.push({ name: 'stocktakes' })
}

async function run(action: () => Promise<void>, done: string): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    await action()
    await load(done)
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

const start = (): Promise<void> =>
  run(() => startStocktake(takeId.value), '已开始盘点，账面按当前库存重新拍了一次')

const cancel = (): Promise<void> =>
  run(() => cancelStocktake(takeId.value), '盘点单已取消')

const confirm = (): Promise<void> =>
  run(async () => {
    await confirmStocktake(takeId.value)
  }, '差异已调账，盘点单已归档')

/** 逐行保存实盘数：失败时不动本地值，用户还能看到刚才填的内容重试 */
async function saveItem(item: ItemDetail): Promise<void> {
  if (item.counted_qty === null || item.counted_qty < 0) return
  error.value = null
  try {
    await putStocktakeItem(takeId.value, item.id, item.counted_qty)
    notice.value = `库位 ${item.location_code} 已保存实盘数 ${item.counted_qty}`
  } catch (e) {
    error.value = e
  }
}

/** 按账面一键填：把还没盘的行全部按账面数录入（盘亏盘盈少时省事，之后仍可逐行改） */
async function fillByBook(): Promise<void> {
  const todo = items.value.filter((item) => item.counted_qty === null)
  if (!todo.length) return
  await run(async () => {
    for (const item of todo) {
      await putStocktakeItem(takeId.value, item.id, item.book_qty)
    }
  }, `已按账面填入 ${todo.length} 行，逐行核对后再提交`)
}

/** 提交盘点：把全部实盘数提交并转待确认（后端会校验每行都录过） */
const submit = (): Promise<void> =>
  run(
    () =>
      countStocktake(
        takeId.value,
        items.value
          .filter((item) => item.counted_qty !== null)
          .map((item) => ({ id: item.id, counted_qty: item.counted_qty as number })),
      ),
    '已提交盘点，确认差异后系统会按差异调账',
  )

const countedCount = computed(() => items.value.filter((i) => i.counted_qty !== null).length)
const uncounted = computed(() => items.value.length - countedCount.value)
const diffItems = computed(
  () => items.value.filter((i) => (diffOf(i) ?? 0) !== 0),
)
const bookTotal = computed(() => items.value.reduce((sum, i) => sum + i.book_qty, 0))
const countedTotal = computed(() =>
  items.value.reduce((sum, i) => sum + (i.counted_qty ?? 0), 0),
)
const netDiff = computed(() => countedTotal.value - bookTotal.value)

const canStart = computed(() => take.value?.status === 'draft')
const canCount = computed(() => take.value?.status === 'counting')
const canSubmit = computed(() => canCount.value && uncounted.value === 0 && items.value.length > 0)
const canConfirm = computed(() => take.value?.status === 'confirming')
const canCancel = computed(
  () => take.value?.status === 'draft' || take.value?.status === 'counting',
)

const scopeText = computed(() => {
  const t = take.value
  if (!t) return ''
  const base = SCOPE_LABELS[t.scope] ?? t.scope
  if (t.scope === 'zone') return `${base} · ${t.zone}`
  return base
})
</script>

<template>
  <div class="yc-std" v-loading="loading">
    <header class="yc-std__bar">
      <el-button @click="back">返回列表</el-button>
      <template v-if="take">
        <span class="yc-std__docno">{{ take.doc_no }}</span>
        <el-tag :type="STATUS_TAG[take.status as StocktakeStatus]" size="small">
          {{ STATUS_LABELS[take.status as StocktakeStatus] ?? take.status }}
        </el-tag>
        <div class="yc-std__spacer" />
        <el-button v-if="canCount" :disabled="items.length === 0" @click="fillByBook">
          按账面填充
        </el-button>
        <el-button
          v-if="canCount"
          v-perm="'wms:stocktake:count'"
          :disabled="!canSubmit"
          :loading="busy"
          type="primary"
          @click="submit"
        >
          提交盘点
        </el-button>
        <el-button v-if="canStart" v-perm="'wms:stocktake:start'" :loading="busy" type="primary" @click="start">
          开始盘点
        </el-button>
        <el-popconfirm
          v-if="canConfirm"
          :title="`确认按差异调账并归档？共 ${diffItems.length} 行有差异，净差异 ${netDiff >= 0 ? '+' : ''}${netDiff}。`"
          width="360"
          @confirm="confirm"
        >
          <template #reference>
            <el-button v-perm="'wms:stocktake:confirm'" :loading="busy" type="primary">确认差异</el-button>
          </template>
        </el-popconfirm>
        <el-popconfirm
          v-if="canCancel"
          title="确认取消该盘点单？"
          width="260"
          @confirm="cancel"
        >
          <template #reference>
            <el-button v-perm="'wms:stocktake:cancel'" type="danger">取消盘点</el-button>
          </template>
        </el-popconfirm>
      </template>
    </header>

    <div v-if="error" class="yc-std__error">
      <TraceIdAlert :error="error" />
    </div>
    <div v-if="notice" class="yc-std__notice">{{ notice }}</div>

    <template v-if="detail && take">
      <el-descriptions class="yc-std__meta" :column="4" border>
        <el-descriptions-item label="仓库">
          {{ detail.warehouse_name || `#${take.warehouse_id}` }}
        </el-descriptions-item>
        <el-descriptions-item label="盘点范围">{{ scopeText }}</el-descriptions-item>
        <el-descriptions-item label="盘点行数">{{ items.length }}</el-descriptions-item>
        <el-descriptions-item label="已录行数">{{ countedCount }}</el-descriptions-item>
        <el-descriptions-item label="账面合计">{{ bookTotal }}</el-descriptions-item>
        <el-descriptions-item label="实盘合计">{{ countedTotal }}</el-descriptions-item>
        <el-descriptions-item label="净差异">
          <span :class="netDiff === 0 ? '' : 'yc-std__diff'">
            {{ netDiff >= 0 ? '+' : '' }}{{ netDiff }}
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="有差异行">{{ diffItems.length }}</el-descriptions-item>
        <el-descriptions-item label="创建人">{{ take.created_by }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDateTime(take.created_at) }}</el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ take.remark || '—' }}</el-descriptions-item>
      </el-descriptions>

      <div v-if="canCount && uncounted > 0" class="yc-std__tip">
        还有 {{ uncounted }} 行没有实盘数，全部录完才能提交。
      </div>

      <el-card class="yc-std__card">
        <template #header>
          <span>盘点明细</span>
          <span class="yc-std__hint">
            {{ canCount ? '填完实盘数回车或点别处即保存' : '差异 = 实盘 - 账面' }}
          </span>
        </template>
        <el-table :data="items" row-key="id">
          <el-table-column prop="location_code" label="库位" width="120" />
          <el-table-column prop="sku_code" label="商品编码" width="140" />
          <el-table-column prop="sku_name" label="商品名称" min-width="150" show-overflow-tooltip />
          <el-table-column prop="batch_no" label="批次" width="120">
            <template #default="{ row }">{{ row.batch_no || '—' }}</template>
          </el-table-column>
          <el-table-column prop="book_qty" label="账面" width="90" align="right" />
          <el-table-column label="实盘" width="150">
            <template #default="{ row }">
              <el-input-number
                v-if="canCount"
                v-model="row.counted_qty"
                :min="0"
                :controls="false"
                size="small"
                class="yc-std__qty"
                @change="saveItem(row)"
              />
              <template v-else>{{ row.counted_qty ?? '—' }}</template>
            </template>
          </el-table-column>
          <el-table-column label="差异" width="90" align="right">
            <template #default="{ row }">
              <span
                :class="{
                  'yc-std__diff': (diffOf(row) ?? 0) !== 0,
                  'yc-std__zero': (diffOf(row) ?? 0) === 0 && row.counted_qty !== null,
                }"
              >
                {{ diffOf(row) === null ? '—' : diffOf(row) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="unit" label="单位" width="80" />
        </el-table>
      </el-card>
    </template>
  </div>
</template>

<style scoped lang="scss">
.yc-std {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-3);
  }

  &__docno {
    font-weight: 600;
  }

  &__spacer {
    flex: 1;
  }

  &__meta {
    margin-bottom: var(--yc-space-4);
  }

  &__card {
    margin-bottom: var(--yc-space-4);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__notice {
    margin-bottom: var(--yc-space-3);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__tip {
    margin-bottom: var(--yc-space-3);
    color: var(--yc-text-warning, #e6a23c);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    margin-left: var(--yc-space-2);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__qty {
    width: 100%;
  }

  &__diff {
    color: var(--yc-color-danger, #f56c6c);
    font-weight: 600;
  }

  &__zero {
    color: var(--yc-text-secondary);
  }
}
</style>
